<template>
  <div v-if="activity" class="activity-context" :class="activityTypeClass">
    <div class="activity-icon-wrapper" :class="activityTypeClass">
      <span class="activity-type-icon">{{ activityIcon }}</span>
    </div>

    <div class="activity-details">
      <div class="activity-header">
        <span class="activity-label">{{ activity.label }}</span>
        <span v-if="activity.type === 'match'" class="activity-opponent">
          vs {{ activity.opposition }}
        </span>
      </div>

      <div class="activity-meta">
        <span v-if="activity.time" class="activity-time">
          <span class="meta-icon">—</span> {{ formatTime(activity.time) }}
        </span>
        <span v-if="activity.location" class="activity-location">
          <span class="meta-icon location-dot"></span> {{ activity.location }}
        </span>
      </div>

      <div class="nutrition-advice" :style="adviceStyle">
        <span class="advice-icon"></span>
        <span class="advice-text">{{ nutritionAdvice }}</span>
      </div>
    </div>

    <!-- Days until indicator -->
    <div v-if="activity.days_to_match !== null" class="days-indicator">
      <div class="days-value">
        {{ Math.abs(activity.days_to_match) }}
      </div>
      <div class="days-label">
        {{ activity.days_to_match >= 0 ? 'days to match' : 'days since match' }}
      </div>
    </div>
  </div>

  <div v-else class="no-activity">
    <span class="no-activity-icon"></span>
    <span class="no-activity-text">No scheduled activity</span>
  </div>
</template>

<script>
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "ActivityContext",

  props: {
    activity: {
      type: Object,
      default: null,
    },
  },

  computed: {
    activityTypeClass() {
      if (!this.activity) return "";
      return `activity-${this.activity.type || "rest"}`;
    },

    activityIcon() {
      if (!this.activity) return "—";

      switch (this.activity.type) {
        case "match":
          return "M";
        case "training":
          return "T";
        default:
          return "R";
      }
    },

    contextInfo() {
      if (!this.activity) return null;
      return Nutrition.getContextInfo(this.activity.context);
    },

    adviceStyle() {
      if (!this.contextInfo) return {};
      return {
        backgroundColor: this.contextInfo.bgColor,
        borderColor: this.contextInfo.color,
      };
    },

    nutritionAdvice() {
      if (!this.activity) return "";

      const context = this.activity.context;

      switch (context) {
        case "match_day":
          return "High carbs (6-8g/kg). Pre-match meal 3-4 hrs before. Stay hydrated!";
        case "carb_loading":
          return "Carb loading day! Aim for 6-8g/kg carbs to maximize glycogen stores.";
        case "preparation":
          return "Begin increasing carbs. Light, easy-to-digest meals.";
        case "recovery_day":
          return "Focus on recovery. High carbs + protein within 60 mins of activity.";
        case "training_day":
          return "Standard training nutrition. Pre-training snack 1-2 hrs before.";
        case "rest_day":
          return "Lower energy needs today. Maintain protein, reduce carbs slightly.";
        default:
          return this.contextInfo?.description || "Balanced nutrition for optimal performance.";
      }
    },
  },

  methods: {
    formatTime(time) {
      if (!time) return "";
      // Handle HH:MM:SS format
      const parts = time.split(":");
      if (parts.length >= 2) {
        const hours = parseInt(parts[0]);
        const mins = parts[1];
        const ampm = hours >= 12 ? "PM" : "AM";
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${mins} ${ampm}`;
      }
      return time;
    },
  },
};
</script>

<style scoped lang="scss">
.activity-context {
  display: flex;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #6c757d;

  &.activity-match {
    border-left-color: #dc3545;
    background: linear-gradient(to right, #fff5f5, white);
  }

  &.activity-training {
    border-left-color: #28a745;
    background: linear-gradient(to right, #f0fff4, white);
  }

  &.activity-rest {
    border-left-color: #6c757d;
    background: linear-gradient(to right, #f8f9fa, white);
  }
}

.activity-icon-wrapper {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #6c757d;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.activity-match {
    background: linear-gradient(135deg, #dc3545, #c82333);
  }

  &.activity-training {
    background: linear-gradient(135deg, #28a745, #1e7e34);
  }

  &.activity-rest {
    background: linear-gradient(135deg, #6c757d, #545b62);
  }
}

.activity-type-icon {
  font-size: 1.2rem;
  font-weight: 700;
  color: white;
}

.activity-details {
  flex: 1;
  min-width: 0;
}

.activity-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.activity-label {
  font-weight: 700;
  font-size: 1rem;
  color: #333;
}

.activity-opponent {
  font-weight: 600;
  color: #dc3545;
  font-size: 0.95rem;
}

.activity-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.85rem;
  color: #666;
  margin-bottom: 10px;
}

.meta-icon {
  font-size: 0.9em;
  color: #999;

  &.location-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    background: #999;
    border-radius: 50%;
    vertical-align: middle;
  }
}

.activity-time,
.activity-location {
  display: flex;
  align-items: center;
  gap: 4px;
}

.nutrition-advice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #f8f9fa;
}

.advice-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid currentColor;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.5;

  &::before {
    content: "i";
    font-size: 0.7rem;
    font-weight: 700;
    font-style: italic;
  }
}

.advice-text {
  font-size: 0.85rem;
  color: #333;
  line-height: 1.4;
}

.days-indicator {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: #f0f0f0;
  border-radius: 8px;
  min-width: 70px;
}

.days-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
}

.days-label {
  font-size: 0.65rem;
  color: #666;
  text-align: center;
  text-transform: uppercase;
}

.no-activity {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  background: #f8f9fa;
  border-radius: 12px;
  color: #666;
}

.no-activity-icon {
  width: 24px;
  height: 24px;
  border: 2px solid #999;
  border-radius: 4px;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 2px;
    background: #999;
  }
}

.no-activity-text {
  font-size: 0.9rem;
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .activity-context {
    flex-wrap: wrap;
    padding: 12px;
    gap: 12px;
  }

  .activity-icon-wrapper {
    width: 40px;
    height: 40px;
  }

  .activity-type-icon {
    font-size: 1.2rem;
  }

  .days-indicator {
    width: 100%;
    flex-direction: row;
    justify-content: center;
    gap: 8px;
    padding: 8px;
  }

  .days-value {
    font-size: 1.2rem;
  }

  .days-label {
    font-size: 0.75rem;
  }
}
</style>
